import { usePersistedState, useScrollMemory } from '../../hooks/usePersistedState';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Table,
  Select,
  Space,
  Row,
  Col,
  Typography,
  Card,
  Modal,
  Input,
  message,
  Popconfirm,
} from 'antd';
import { PlusOutlined, StopOutlined, CloseCircleOutlined, CheckCircleOutlined, SendOutlined, DeleteOutlined, ExpandAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import { costCenterLabel } from '../../api';
import { listPOs, getLookups, bulkUpdatePOStatus, deletePO } from '../../api';
import type { POListRow, POListLine, POListAttention } from '../../api';
import { useAuth } from '../../context/AuthContext';
import ConfigurableTable from '../../components/ConfigurableTable';
import type { ConfigurableColumn } from '../../components/ConfigurableTable';
import {
  DocCell,
  StatusPill,
  MoneyCell,
  MissingValue,
  ItemCell,
  VendorCell,
  ProgressCell,
  FilterChips,
  DensityToggle,
  useDensity,
  MONO_FONT,
} from '../../components/table-cells';
import type { Chip } from '../../components/table-cells';

const { Title, Text } = Typography;
const { TextArea } = Input;

/** Chip order on the list header. */
const PO_STATUS_ORDER = ['Open', 'Ordered', 'Partially Received', 'Received', 'Closed', 'Cancelled'];

/** What the server counts as still open, mirrored here so the chips and the rows agree. */
const OPEN_STATUSES = ['Open', 'Ordered', 'Partially Received'];

interface Department {
  id: number;
  name: string;
}

interface Vendor {
  id: number;
  name: string;
}

const fmtMoney = (v: any) => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

/** "on estimate" when the actual matches, otherwise the signed percentage away from it. */
const varianceLabel = (actual: number, estimate: number): string => {
  if (Math.abs(actual - estimate) < 0.005) return 'on estimate';
  const pct = ((actual - estimate) / estimate) * 100;
  return `${pct > 0 ? '+' : '−'}${Math.abs(pct).toFixed(0)}%`;
};

/** A line typed by hand: no catalogue link and no code to show. */
const FreeTextMark: React.FC = () => (
  <span
    style={{
      display: 'inline-block',
      border: '1px dashed #cfd4dd',
      borderRadius: 4,
      padding: '0 5px',
      marginBottom: 3,
      font: `500 11px ${MONO_FONT}`,
      color: '#8a94a6',
    }}
  >
    free text
  </span>
);

/** Lines of one PO, shown under the row when expanded. */
const POLinesTable: React.FC<{ lines: POListLine[]; compact: boolean }> = ({ lines, compact }) => (
  <Table<POListLine>
    size="small"
    pagination={false}
    rowKey="id"
    dataSource={lines || []}
    style={{ margin: '4px 0 4px 24px' }}
    columns={[
      { title: '#', dataIndex: 'lineNo', width: 40, align: 'center' as const },
      {
        title: 'Item',
        key: 'item',
        render: (_: any, l: POListLine) => {
          const catalogItem = l.mrLine?.catalogItem ?? null;
          const freeText = !catalogItem && !l.partNumber;
          return (
            <div>
              {freeText && <FreeTextMark />}
              <ItemCell
                partNumber={catalogItem?.partNumber ?? l.partNumber}
                description={catalogItem?.description ?? l.itemDescription}
                descriptionAr={catalogItem?.descriptionAr}
                linkTo={catalogItem?.id ? `/catalog/${catalogItem.id}` : undefined}
                compact={compact}
              />
            </div>
          );
        },
      },
      {
        title: 'Source MR',
        key: 'mr',
        width: 170,
        render: (_: any, l: POListLine) => {
          const header = l.mrLine?.mrHeader;
          if (!header) return <MissingValue label="no source MR" compact={compact} />;
          return (
            <span>
              <Link to={`/mrs/${header.mrId}`} style={{ font: `500 12.5px ${MONO_FONT}` }}>
                {header.mrId}
              </Link>
              {l.mrLine?.lineNo ? (
                <span style={{ color: '#8a94a6', fontSize: 11.5 }}> · line {l.mrLine.lineNo}</span>
              ) : null}
            </span>
          );
        },
      },
      {
        title: 'Qty',
        key: 'qty',
        width: 100,
        align: 'right' as const,
        render: (_: any, l: POListLine) => `${Number(l.qty)} ${l.unit?.name || ''}`.trim(),
      },
      {
        title: 'Price',
        key: 'price',
        width: 140,
        align: 'right' as const,
        render: (_: any, l: POListLine) => {
          const estimate = l.unitPrice == null ? null : Number(l.unitPrice);
          const amount = l.actualUnitPrice != null ? Number(l.actualUnitPrice) : estimate;
          const hasEstimate = estimate != null && estimate > 0;
          const ruleText =
            hasEstimate && amount != null
              ? `est. ${fmtMoney(estimate)} · ${varianceLabel(amount, estimate as number)}`
              : null;
          const over = hasEstimate && amount != null && amount > (estimate as number) + 0.005;
          return (
            <MoneyCell
              align="right"
              compact={compact}
              amount={amount}
              currency={l.currency?.code}
              rule={ruleText ? (over ? <span style={{ color: '#b42323' }}>{ruleText}</span> : ruleText) : null}
            />
          );
        },
      },
      {
        title: 'Received',
        key: 'recv',
        width: 110,
        align: 'right' as const,
        render: (_: any, l: POListLine) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {Number(l.totalReceived || 0)} / {Number(l.qty || 0)}
          </span>
        ),
      },
      {
        title: 'Cost center',
        key: 'cc',
        width: 200,
        ellipsis: true,
        render: (_: any, l: POListLine) =>
          l.costCenter ? costCenterLabel(l.costCenter) : <MissingValue label="no cost center" compact={compact} />,
      },
    ]}
  />
);

const POList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isApprover, canAccess, myDepartmentIds } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState<POListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [attentionCounts, setAttentionCounts] = useState<POListAttention>({ overEstimate: 0, late: 0, openValue: 0, lineCancellations: 0 });
  const [page, setPage] = usePersistedState('pos', 'page', 1);
  const [pageSize, setPageSize] = usePersistedState('pos', 'pageSize', 20);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filterDepts, setFilterDepts] = usePersistedState<number[]>('pos', 'depts', []);
  const [statusChip, setStatusChip] = usePersistedState<string | null>('pos', 'statusChip', null);
  const [attention, setAttention] = usePersistedState<string[]>('pos', 'attention', []);
  const [filterVendors, setFilterVendors] = usePersistedState<number[]>('pos', 'vendors', []);
  useScrollMemory('pos', !loading);
  const [expandedKeys, setExpandedKeys] = usePersistedState<number[]>('pos', 'expanded', []);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [density, setDensity] = useDensity('po-list');
  const compact = density === 'compact';

  const handleDeletePO = async (record: POListRow) => {
    try {
      await deletePO(record.id);
      message.success(`PO ${record.poId} deleted`);
      fetchPOs();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete PO');
    }
  };

  const handleBulkDeletePOs = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkDeleting(true);
    let successCount = 0;
    const errors: string[] = [];
    for (const id of selectedRowKeys) {
      try {
        await deletePO(id);
        successCount++;
      } catch (err: any) {
        errors.push(`PO #${id}: ${err.response?.data?.error || err.message}`);
      }
    }
    if (successCount > 0) message.success(`${successCount} PO(s) deleted`);
    if (errors.length > 0) message.error(`Failed: ${errors.join('; ')}`);
    setSelectedRowKeys([]);
    setBulkDeleting(false);
    fetchPOs();
  };

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const lookups = await getLookups();
        setDepartments(lookups.departments);
        setVendors(lookups.vendors);
      } catch (err) {
        console.error('Failed to load lookups', err);
      }
    };
    loadLookups();
  }, []);

  const fetchPOs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: pageSize };
      if (filterDepts.length > 0) params.departmentIds = filterDepts.join(',');
      if (filterVendors.length > 0) params.vendorIds = filterVendors.join(',');
      if (statusChip) params.status = statusChip;

      const result = await listPOs(params);
      setPos(result.data || []);
      setTotal(result.total || 0);
      setStatusCounts(result.statusCounts || {});
      if (result.attention) setAttentionCounts(result.attention);
    } catch (err) {
      console.error('Failed to load POs', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterDepts, statusChip, filterVendors]);

  useEffect(() => {
    fetchPOs();
  }, [fetchPOs]);

  /** None of the three attention buckets has a server filter — they narrow the page that was loaded. */
  const rows = useMemo(() => {
    if (attention.length === 0) return pos;
    return pos.filter((p) => {
      // Net of cancelled (2026-09-22): kept actual vs kept estimate, same rule as the server count.
      if (attention.includes('overEstimate') && !(p.actualTotal != null && Number(p.netValue ?? p.actualTotal) > Number(p.netEstimate ?? p.poTotal ?? 0) + 0.005)) {
        return false;
      }
      if (attention.includes('late') && !(p.lateFlag && OPEN_STATUSES.includes(p.status))) return false;
      if (attention.includes('openValue') && !(Number(p.openValue ?? 0) > 0 && OPEN_STATUSES.includes(p.status))) {
        return false;
      }
      if (attention.includes('lineCancellations') && !(p.lines || []).some((l: any) => l.cancelStatus === 'Pending')) return false;
      return true;
    });
  }, [pos, attention]);

  const scopeTotal = useMemo(
    () => Object.values(statusCounts).reduce((sum, n) => sum + n, 0) || total,
    [statusCounts, total],
  );

  const openCount = useMemo(
    () => OPEN_STATUSES.reduce((sum, s) => sum + (statusCounts[s] ?? 0), 0),
    [statusCounts],
  );

  // Spec order first, then any other status that actually exists in this scope (e.g. Pending Cancellation),
  // so no PO becomes unreachable from the chips.
  const statusChips: Chip[] = [
    ...PO_STATUS_ORDER.map((s) => ({ key: s, label: s, count: statusCounts[s] ?? 0 })),
    ...Object.keys(statusCounts)
      .filter((s) => PO_STATUS_ORDER.indexOf(s) < 0 && (statusCounts[s] ?? 0) > 0)
      .sort()
      .map((s) => ({ key: s, label: s, count: statusCounts[s] })),
  ];

  const attentionChips: Chip[] = [
    { key: 'overEstimate', label: 'Over estimate', tone: 'bad', count: attentionCounts.overEstimate },
    { key: 'late', label: 'Late', tone: 'warn', count: attentionCounts.late },
    { key: 'openValue', label: 'Open value', tone: 'info', count: attentionCounts.openValue },
    { key: 'lineCancellations', label: 'Line cancellations', tone: 'warn', count: attentionCounts.lineCancellations ?? 0 },
  ];

  const columns: ConfigurableColumn<POListRow>[] = [
    {
      title: 'PO',
      key: 'poId',
      width: 170,
      hideable: false,
      render: (_: any, record: POListRow) => {
        const age = record.poAgeDays != null ? `${record.poAgeDays} days` : null;
        const late = record.lateFlag ? (
          <span style={{ color: '#9a5b00', fontWeight: 600, letterSpacing: '.03em' }}>
            {age ? ' · ' : ''}LATE
          </span>
        ) : null;
        return (
          <DocCell
            id={record.poId || `PO-${String(record.id).padStart(4, '0')}`}
            linkTo={`/pos/${record.id}`}
            date={record.poDate || record.createdAt}
            compact={compact}
            meta={age || late ? <>{age}{late}</> : undefined}
          />
        );
      },
    },
    {
      title: 'Vendor',
      key: 'vendor',
      width: 190,
      render: (_: any, record: POListRow) => (
        <VendorCell
          name={record.vendor?.name}
          appCode={record.vendor?.appCode}
          linkTo={record.vendor ? `/vendors/${record.vendor.id}` : undefined}
        />
      ),
    },
    {
      title: 'Department',
      key: 'departments',
      width: 160,
      render: (_: any, record: POListRow) => {
        // The departments that actually asked for the lines, falling back to the PO header.
        const depts = new Map<number, string>();
        record.lines?.forEach((line) => {
          const dept = line.mrLine?.mrHeader?.department;
          if (dept && !depts.has(dept.id)) depts.set(dept.id, dept.name);
        });
        if (depts.size === 0) {
          return record.department?.name ?? <MissingValue label="no department" compact={compact} />;
        }
        return <span>{Array.from(depts.values()).join(' · ')}</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (val: string) => <StatusPill status={val} />,
    },
    {
      title: 'Value',
      key: 'value',
      width: 150,
      align: 'right' as const,
      render: (_: any, record: POListRow) => {
        const currency = record.lines?.[0]?.currency?.code ?? undefined;
        const grossEstimate = record.poTotal == null ? null : Number(record.poTotal);
        const grossAmount = record.actualTotal != null ? Number(record.actualTotal) : grossEstimate;
        if (grossAmount == null) {
          return (
            <div style={{ textAlign: 'right' }}>
              <MissingValue label="no value" compact={compact} />
            </div>
          );
        }
        // Net of cancelled (2026-09-22): the bold figure is what is still ordered; the stored
        // total stays as ordered and is named in the small print together with the cancelled part.
        const cancelled = Number(record.cancelledValue ?? 0);
        const amount = record.netValue != null ? Number(record.netValue) : grossAmount;
        const estimate = record.netEstimate != null ? Number(record.netEstimate) : grossEstimate;
        const hasEstimate = estimate != null && estimate > 0;
        const varianceText = hasEstimate && record.actualTotal != null
          ? `est. ${fmtMoney(estimate)} · ${varianceLabel(amount, estimate as number)}`
          : null;
        const over = hasEstimate && record.actualTotal != null && amount > (estimate as number) + 0.005;
        const cancelledText = cancelled > 0.005
          ? (
            <>
              {fmtMoney(grossAmount)} ordered ·{' '}
              <span style={{ color: '#b42323' }}>{amount <= 0.005 ? 'all cancelled' : `${fmtMoney(cancelled)} cancelled`}</span>
            </>
          )
          : null;
        const rule = varianceText || cancelledText
          ? (
            <>
              {varianceText ? (over ? <span style={{ color: '#b42323' }}>{varianceText}</span> : varianceText) : null}
              {varianceText && cancelledText ? <br /> : null}
              {cancelledText}
            </>
          )
          : null;
        return (
          <MoneyCell
            align="right"
            compact={compact}
            amount={amount}
            currency={currency}
            rule={rule}
          />
        );
      },
    },
    {
      title: 'Received',
      key: 'receivedValue',
      width: 180,
      render: (_: any, record: POListRow) => (
        // Net of cancelled (2026-09-22): the bar measures against what is still ordered,
        // so a PO closed by a balance cancellation reads complete.
        <ProgressCell
          done={Number(record.receivedValue || 0)}
          total={Number(record.netValue ?? record.actualTotal ?? record.poTotal ?? 0)}
          format={fmtMoney}
          compact={compact}
        />
      ),
    },
    {
      title: 'From MR',
      key: 'fromMr',
      width: 160,
      render: (_: any, record: POListRow) => {
        const mrId = record.firstMrId || record.lines?.[0]?.mrLine?.mrHeader?.mrId || null;
        if (!mrId) return <MissingValue label="no source MR" compact={compact} />;
        return (
          <Link to={`/mrs/${mrId}`} style={{ font: `500 12.5px ${MONO_FONT}` }}>
            {mrId}
          </Link>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            title: 'Delete',
            key: 'delete',
            width: 80,
            render: (_: any, record: POListRow) => (
              <Popconfirm
                title="Are you sure you want to delete this PO?"
                description="This action cannot be undone."
                onConfirm={() => handleDeletePO(record)}
                okText="Yes, Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  const handleBulkClose = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkLoading(true);
    try {
      await bulkUpdatePOStatus(selectedRowKeys, 'Closed');
      message.success(`${selectedRowKeys.length} PO(s) closed`);
      setSelectedRowKeys([]);
      fetchPOs();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to close POs');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkOrder = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkLoading(true);
    try {
      await bulkUpdatePOStatus(selectedRowKeys, 'Ordered');
      message.success(`${selectedRowKeys.length} PO(s) marked as Ordered`);
      setSelectedRowKeys([]);
      fetchPOs();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to mark POs as Ordered');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkRequestCancellation = async () => {
    if (selectedRowKeys.length === 0 || !cancelReason.trim()) {
      message.warning('Please provide a reason');
      return;
    }
    setBulkLoading(true);
    try {
      await bulkUpdatePOStatus(selectedRowKeys, 'Pending Cancellation', cancelReason.trim());
      message.success(`${selectedRowKeys.length} PO(s) sent for cancellation approval`);
      setSelectedRowKeys([]);
      setCancelModalOpen(false);
      setCancelReason('');
      fetchPOs();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to request cancellation');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkApproveCancellation = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkLoading(true);
    try {
      await bulkUpdatePOStatus(selectedRowKeys, 'Cancelled');
      message.success(`${selectedRowKeys.length} PO(s) cancellation approved`);
      setSelectedRowKeys([]);
      fetchPOs();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to approve cancellation');
    } finally {
      setBulkLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as number[]),
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Purchase Orders
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {scopeTotal.toLocaleString('en-US')} total · {openCount.toLocaleString('en-US')} open
          </Text>
        </Col>
        <Col>
          <Space>
            <DensityToggle value={density} onChange={setDensity} />
            {canAccess('purchasing.pos', 'create') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/pos/new')}
              >
                Create PO
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          {(canAccess('purchasing.pos', 'full') || myDepartmentIds.length > 1) && (
            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder="Department"
              allowClear
              style={{ minWidth: 180 }}
              onChange={(val: number[]) => {
                setFilterDepts(val);
                setPage(1);
              }}
              value={filterDepts}
              options={departments
                .filter((d) => canAccess('purchasing.pos', 'full') || myDepartmentIds.includes(d.id))
                .map((d) => ({ label: d.name, value: d.id }))}
              optionFilterProp="label"
            />
          )}
          <Select
            mode="multiple"
            maxTagCount="responsive"
            placeholder="Vendor"
            allowClear
            style={{ minWidth: 200 }}
            onChange={(val: number[]) => {
              setFilterVendors(val);
              setPage(1);
            }}
            value={filterVendors}
            options={vendors.map((v) => ({ label: v.name, value: v.id }))}
            showSearch
            optionFilterProp="label"
          />
          {expandedKeys.length > 0 && pos.some((p) => expandedKeys.includes(p.id))
            ? <Button icon={<ShrinkOutlined />} onClick={() => setExpandedKeys([])}>Collapse all</Button>
            : <Button icon={<ExpandAltOutlined />} onClick={() => setExpandedKeys(pos.map((p) => p.id))}>Expand all</Button>}
        </Space>
      </Card>

      <FilterChips
        style={{ marginBottom: 12 }}
        chips={statusChips}
        value={statusChip}
        onChange={(key) => {
          setStatusChip(key);
          setPage(1);
        }}
        allCount={scopeTotal}
        attention={attentionChips}
        attentionValue={attention}
        onAttentionChange={(keys) => {
          setAttention(keys);
          setPage(1);
        }}
      />

      {selectedRowKeys.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>{selectedRowKeys.length} PO(s) selected</Text>
            {canAccess('purchasing.pos', 'full') && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={bulkLoading}
                onClick={handleBulkOrder}
              >
                Mark as Ordered
              </Button>
            )}
            {isAdmin && (
              <Button
                icon={<StopOutlined />}
                loading={bulkLoading}
                onClick={handleBulkClose}
              >
                Close Selected
              </Button>
            )}
            {canAccess('purchasing.pos', 'full') && (
              <Button
                danger
                icon={<SendOutlined />}
                loading={bulkLoading}
                onClick={() => {
                  setCancelReason('');
                  setCancelModalOpen(true);
                }}
              >
                Request Cancellation
              </Button>
            )}
            {(isApprover || isAdmin) && (
              <Button
                danger
                icon={<CloseCircleOutlined />}
                loading={bulkLoading}
                onClick={handleBulkApproveCancellation}
              >
                Approve Cancellation
              </Button>
            )}
            {isAdmin && (
              <Popconfirm
                title={`Are you sure you want to delete ${selectedRowKeys.length} PO(s)?`}
                description="This action cannot be undone."
                onConfirm={handleBulkDeletePOs}
                okText="Yes, Delete All"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button danger icon={<DeleteOutlined />} loading={bulkDeleting}>
                  Delete Selected
                </Button>
              </Popconfirm>
            )}
            <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
          </Space>
        </Card>
      )}

      <ConfigurableTable<POListRow>
        tableId="po-list"
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(keys as number[]),
          expandedRowRender: (record: POListRow) => <POLinesTable lines={record.lines} compact={compact} />,
          rowExpandable: (record: POListRow) => (record.lines?.length ?? record.lineCount ?? 0) > 0,
        }}
        rowSelection={rowSelection}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (t) => `Total ${t} records`,
        }}
        size={compact ? 'small' : 'middle'}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="Request Cancellation - Reason Required"
        open={cancelModalOpen}
        onOk={handleBulkRequestCancellation}
        onCancel={() => {
          setCancelModalOpen(false);
          setCancelReason('');
        }}
        confirmLoading={bulkLoading}
        okText="Submit Request"
        okButtonProps={{ danger: true }}
      >
        <p>Please provide a reason for requesting cancellation of {selectedRowKeys.length} PO(s). This will be sent to the CEO for approval.</p>
        <TextArea
          rows={4}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Enter cancellation reason..."
        />
      </Modal>
    </div>
  );
};

export default POList;
